import { type ComponentProps, type ReactNode, useState } from "react";

import { Dialog, DialogContent } from "common/components/ui/Dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import { typedObjectEntries } from "common/helpers/utils";
import type { ActionsLeafKey, ActionsType } from "common/types";

type DialogSize = ComponentProps<typeof DialogContent>["size"];

interface DesktopActionsProps<EditOptions extends ActionsType> {
  triggerElement: ReactNode;
  editOptions: EditOptions;
  children: (
    action: ActionsLeafKey<EditOptions>,
    onClose: () => void,
  ) => JSX.Element;
  getDialogSize?: (action: ActionsLeafKey<EditOptions>) => DialogSize;
  contentClassName?: string;
}

export default function DesktopActions<EditOptions extends ActionsType>({
  triggerElement,
  editOptions,
  children,
  getDialogSize,
  contentClassName,
}: DesktopActionsProps<EditOptions>) {
  const [actionState, setActionState] = useState<{
    isOpen: boolean;
    action: ActionsLeafKey<EditOptions> | undefined;
  }>({ isOpen: false, action: undefined });

  const { isOpen, action } = actionState;

  const closeDialog = () =>
    setActionState((prev) => ({ ...prev, isOpen: false }));

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{triggerElement}</DropdownMenuTrigger>

        <DropdownMenuContent align="end" size="sm" className={contentClassName}>
          <DropdownMenuList>
            {typedObjectEntries(editOptions).map(
              ([key, { icon: Icon, label, subActions, onAction, ...rest }]) => {
                if (subActions) {
                  return (
                    <DropdownMenuSub key={String(key)}>
                      <DropdownMenuSubTrigger>
                        <Icon />
                        {label}
                      </DropdownMenuSubTrigger>

                      <DropdownMenuSubContent size="sm" sideOffset={12}>
                        <DropdownMenuList>
                          {typedObjectEntries(subActions).map(
                            ([
                              subKey,
                              {
                                icon: SubIcon,
                                label: subLabel,
                                onAction: subOnAction,
                                ...subRest
                              },
                            ]) => (
                              <DropdownMenuItem
                                key={String(subKey)}
                                onSelect={() =>
                                  subOnAction
                                    ? subOnAction()
                                    : setActionState({
                                        isOpen: true,
                                        action:
                                          subKey as ActionsLeafKey<EditOptions>,
                                      })
                                }
                                variant={
                                  "isDanger" in subRest ? "danger" : "default"
                                }
                              >
                                <SubIcon />
                                {subLabel}
                              </DropdownMenuItem>
                            ),
                          )}
                        </DropdownMenuList>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  );
                }

                return (
                  <DropdownMenuItem
                    key={String(key)}
                    onSelect={() =>
                      onAction
                        ? onAction()
                        : setActionState({
                            isOpen: true,
                            action: key as ActionsLeafKey<EditOptions>,
                          })
                    }
                    variant={"isDanger" in rest ? "danger" : "default"}
                  >
                    <Icon />
                    {label}
                  </DropdownMenuItem>
                );
              },
            )}
          </DropdownMenuList>
        </DropdownMenuContent>
      </DropdownMenu>

      {action && (
        <Dialog
          open={isOpen}
          onOpenChange={(isOpen) =>
            setActionState((prev) => ({ ...prev, isOpen }))
          }
        >
          <DialogContent size={getDialogSize ? getDialogSize(action) : "md"}>
            {children(action, closeDialog)}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
